import React from 'react';
import { MessageCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePermissions } from '@/contexts/unified-permissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useFeedbackChat } from '@/contexts/useFeedbackChat';
import { CHAT_CONFIG } from '@/features/chat/constants';

/**
 * Enhanced floating feedback button with chat functionality and badge count
 * Supports toggling between feedback and chat modes
 */
export const EnhancedFloatingFeedbackButton: React.FC = () => {
  const {
    openFeedback,
    openChat,
    fabMode,
    unreadChatCount
  } = useFeedbackChat();
  const { user } = usePermissions();
  const isMobile = useIsMobile();

  // Only show for authenticated users
  if (!user) return null;

  const handleMainButtonClick = () => {
    if (fabMode === 'chat') {
      openChat();
    } else {
      openFeedback();
    }
  };



  const formatBadgeCount = (count: number): string => {
    if (count > CHAT_CONFIG.MAX_BADGE_DISPLAY) {
      return `${CHAT_CONFIG.MAX_BADGE_DISPLAY}+`;
    }
    return count.toString();
  };

  const shouldShowBadge = fabMode === 'chat' && unreadChatCount > 0;

  return (
    <div 
      className={`fixed right-6 z-50 ${isMobile ? '' : 'bottom-6'}`}
      style={isMobile ? {
        bottom: `calc(6rem + max(env(safe-area-inset-bottom), 0px))`
      } : { bottom: '1.5rem' }}
    >


      {/* Main FAB container */}
      <div className="flex flex-col items-end gap-2">
        {/* Main FAB button */}
        <div className="relative">
          <Button
            onClick={handleMainButtonClick}
            className="h-12 w-12 rounded-full shadow-lg"
            aria-label={fabMode === 'chat' ? 'Open chat' : 'Give feedback'}
          >
            {fabMode === 'chat' ? (
              <MessageCircle className="h-5 w-5" />
            ) : (
              <MessageSquare className="h-5 w-5" />
            )}
          </Button>

          {/* Badge count for unread messages */}
          {shouldShowBadge && (
            <Badge 
              variant="destructive" 
              className={cn(
                "absolute -top-1 -right-1 h-6 min-w-[24px] text-xs px-1.5",
                "flex items-center justify-center rounded-full",
                "shadow-lg border-2 border-background",
                // iOS-style badge styling
                "bg-red-500 text-white font-medium"
              )}
            >
              {formatBadgeCount(unreadChatCount)}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
